

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import html2canvas from "html2canvas";
import Spinner from "../../components/ui/Spinner";
import MessageBox from "../../components/ui/MessageBox";
import Button from "../../components/ui/Button";
import SalesOrderHeader from "./SalesOrderHeader";
import SalesOrderItems from "./SalesOrderItems";
import { XCircle, Printer, Send } from "lucide-react";
import { jsPDF } from "jspdf";
/** --- PrintPreview Component --- */
const PrintPreview = ({ htmlContent, onClose, order }) => {
  const hiddenRef = useRef(null);
  const [waMenuOpen, setWaMenuOpen] = useState(false);
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "F6") {
        e.preventDefault();
        handlePrint();
      } else if (e.key === "F8") {
        e.preventDefault();
        await handleWhatsAppShare();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [order]);



  const handlePrint = () => {
    const iframe = document.getElementById("print-iframe");
    if (iframe?.contentWindow) iframe.contentWindow.print();
  };

  // const handleWhatsAppShare = async () => {
  //   if (!hiddenRef.current || !order?.MobileNo) return;
  //   const mobileNumber = "91" + order.MobileNo.replace(/[^0-9]/g, "");
  //   try {
  //     const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true });
  //     const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  //     if (blob && navigator.clipboard && window.ClipboardItem) {
  //       await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
  //     }
  //     const message = encodeURIComponent("");
  //     window.open(`https://wa.me/${mobileNumber}?text=${message}`, "_blank");
  //   } catch (err) {
  //     console.error("WhatsApp share failed:", err);
  //   }
  // };

  
  const handleWhatsAppClick = () => {
    setWaMenuOpen((prev) => !prev);
  };

  const shareAsImage = async () => {
    setWaMenuOpen(false);
    if (!hiddenRef.current) return alert("Content not ready.");

    const countryCode = "91";
    const mobileNumber = order?.MobileNo?.replace(/[^0-9]/g, "") || "";
    if (!mobileNumber) return alert("Mobile number not available.");

    const fullNumber = countryCode + mobileNumber;

    try {
      const canvas = await html2canvas(hiddenRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (blob) await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);

      alert("Image copied! Paste in WhatsApp.");
      window.open(`https://wa.me/${fullNumber}`, "_blank");
    } catch (err) {
      console.warn(err);
    }
  };

  const shareAsPDF = async () => {
  setWaMenuOpen(false);
  if (!hiddenRef.current) return alert("Content not ready.");

  try {
    // Capture full content with higher resolution
    const canvas = await html2canvas(hiddenRef.current, {
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY,
      windowWidth: hiddenRef.current.scrollWidth, // ✅ ensure full width capture
      windowHeight: hiddenRef.current.scrollHeight,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    // Calculate image size to fit full page width
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    // Add first page
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    // Add remaining pages
    while (heightLeft > 0) {
      position -= pdfHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    // Export or share
    const pdfBlob = pdf.output("blob");
    const file = new File([pdfBlob], "invoice.pdf", { type: "application/pdf" });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: "Invoice PDF",
        text: "Please find attached invoice PDF",
      });
    } else {
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    }
  } catch (err) {
    console.error("PDF generation failed:", err);
    alert("Failed to generate PDF. Please try again.");
  }
};

  return (
    <>
      {/* Hidden copy for canvas */}
      <div
        ref={hiddenRef}
        style={{
  position: "fixed",
  top: "-9999px",
  left: "-9999px",
  width: "1000px", // ✅ wider for full invoice content
  background: "#fff",
  padding: "10px",
  transform: "scale(1)", // ensure no zoom clipping
  transformOrigin: "top left",
}}

        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
      {/* Overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
        <div className="relative w-full max-w-2xl p-4 bg-white rounded-lg shadow-2xl">
          {/* Header */}
          <h3 className="py-3 text-lg font-semibold text-center border-b">Invoice Preview</h3>
          {/* Content */}
          <div className="px-4 py-2 border-b">
            <iframe
              id="print-iframe"
              srcDoc={htmlContent}
              title="Print Preview"
              style={{ width: "100%", height: "60vh", border: "none" }}
            />
          </div>
          {/* Footer */}
          <div className="flex justify-end gap-12 p-5">
            <Button
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
                bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md 
                hover:from-red-600 hover:to-red-700 hover:shadow-lg 
                transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <XCircle size={16} /> Close
            </Button>
            <Button
              onClick={handlePrint}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white
                bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md
                hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg
                transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <Printer size={16} /> Print (F6)
            </Button>
            {/* <Button
              onClick={handleWhatsAppShare}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white
                bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-md
                hover:from-green-600 hover:to-emerald-700 hover:shadow-lg
                transition-all duration-300 ease-in-out transform hover:scale-105"
            >
              <Send size={16} /> WhatsApp (F8)
            </Button> */}

              {/* WhatsApp Button */}
            <div className="relative">
              <Button
                onClick={handleWhatsAppClick}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
      bg-green-500 rounded-lg shadow-md hover:bg-green-600 transition-all duration-300"
  >
                <Send size={16} /> WhatsApp
              </Button>

              {waMenuOpen && (
                <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border rounded-lg shadow-lg w-44">
                  <button
                    onClick={shareAsImage}
                    className="flex items-center w-full gap-2 px-3 py-2 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600"
                  >
                    <img src="/icons/image-icon.svg" alt="image" className="w-4 h-4" /> Share as Image
                  </button>

                  <button
                    onClick={shareAsPDF}
                    className="flex items-center w-full gap-2 px-3 py-2 mt-1 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600"
                  >
                    <img src="/icons/pdf-icon.svg" alt="pdf" className="w-4 h-4" /> Share as PDF
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

const SalesOrderDetailsPage = ({ setView, context }) => {
  const { user } = useAuth();
  const [SalesOrderDetails, setSalesOrderDetails] = useState(null);
  const [salesOrderItems, setSalesOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Accordion states
  const [isSalesOrderHeaderOpen, setIsSalesOrderHeaderOpen] = useState(false);
  const [isSalesOrderItemsOpen, setIsSalesOrderItemsOpen] = useState(true);

  // Print preview state
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printContent, setPrintContent] = useState("");
  const [currentOrder, setCurrentOrder] = useState(null);

const fetchOrderData = useCallback(async (showSpinner = true) => {
  if (showSpinner) setLoading(true);
  try {
    const orderData = await api.getSalesOrderForEdit(context.id);

    const table = orderData?.EditPoOrderTaxjs?.Table || orderData?.Table || [];
    const table1 = orderData?.EditPoOrderTaxjs?.Table1 || orderData?.Table1 || [];
    const table2 = orderData?.EditPoOrderTaxjs?.Table2 || orderData?.Table2 || [];
    const table3 = orderData?.EditPoOrderTaxjs?.Table3 || orderData?.Table3 || [];

    if (table.length > 0) {
      const header = table[0];

      const formatDate = (dateString) => {
        if (!dateString) return "";
        const d = new Date(dateString);
        const month = (d.getMonth() + 1).toString().padStart(2, "0");
        const day = d.getDate().toString().padStart(2, "0");
        const year = d.getFullYear();
        return `${year}-${month}-${day}`;
      };

      const fixedHeader = {
        ...header,
        Podate: formatDate(header.Podate),
        DeliveryDate: formatDate(header.DeliveryDate),
        SubTypeCode: header.SubTypeCode,
        SubTypeName: header.SubTypeName,
      };

      setSalesOrderDetails({
        ...fixedHeader,
        Table1: table1,
        Table2: table2,
        Table3: table3,
      });
      setSalesOrderItems(table1);
    }
  } catch (err) {
    console.error(err);
    setError("Failed to load order data.");
  } finally {
    if (showSpinner) setLoading(false);
  }
}, [context.id]);

  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  const handlePrintInvoice = async () => {
  try {
    const resp = await api.MobGetfinalizeOrder(context.id);
    const content =
      resp?.Table?.[0]?.PrintContent ??
      resp?.Table3?.[0]?.PrintContent ??
      resp?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ??
      resp?.data?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ??
      null;

    if (!content) return alert("Print content not found.");

    setPrintContent(content);

    const newOrder = SalesOrderDetails
      ? { ...SalesOrderDetails, Poid: context.id }
      : { Poid: context.id };
    setCurrentOrder(newOrder);

    const match = content.match(/Bill No\s*-?\s*([^*<\r\n]+)/i);
    if (match) {
      const invNo = match[1].trim();

      setSalesOrderDetails((prev) =>
        prev
          ? { ...prev, InvNo: invNo, Cpid: context.id } 
          : { Poid: context.id, InvNo: invNo, Cpid: context.id }
      );
    }

    setShowPrintPreview(true);
  } catch (err) {
    console.error(err);
    alert("Error fetching print content.");
  }
};
const handleQuotation = async () => {
  try {
    const resp = await api.get(`GetHtmlPoQuotationAPI?PoId=${context.id}`);
    console.log("Quotation API Response:", resp);

    const content = resp?.Table?.[0]?.PoQuotationHtml ?? null;

    if (content) {
      setPrintContent(content);

      const curOrder = SalesOrderDetails
        ? { ...SalesOrderDetails, Poid: context.id }
        : { Poid: context.id };

      setCurrentOrder(curOrder);
      setShowPrintPreview(true);
    } else {
      alert("Quotation content not found.");
    }
  } catch (err) {
    console.error("Error fetching quotation:", err);
    alert("Failed to fetch quotation.");
  }
};
 
const triggerInvoiceGeneration = async () => {
  const curOrder = SalesOrderDetails || {};
  const invoiceExists = curOrder.Cpid && Number(curOrder.Cpid) > 0;

  if (!invoiceExists) {
    if (window.confirm("Invoice not generated yet. Are you really want to generate Invoice?")) {
      await handlePrintInvoice();
    }
  } else {
    await handlePrintInvoice();
  }
};

// --- F10 Shortcut for Quotation ---
useEffect(() => {
  const handleF10 = (e) => {
    if (e.key === "F10") {
      e.preventDefault();
      handleQuotation();
    }
  };

  window.addEventListener("keydown", handleF10);
  return () => window.removeEventListener("keydown", handleF10);
}, [SalesOrderDetails]); 

  useEffect(() => {
  const handleF4 = (e) => {
    if (e.key === "F4") {
      e.preventDefault();
      triggerInvoiceGeneration(); 
    }
  };

  window.addEventListener("keydown", handleF4);
  return () => window.removeEventListener("keydown", handleF4);
}, [SalesOrderDetails]);


  useEffect(() => {
    const handleF7 = (e) => {
      if (e.key === "F7") {
        e.preventDefault();
        setView("salesorders");
      }
    };
    window.addEventListener("keydown", handleF7);
    return () => window.removeEventListener("keydown", handleF7);
  }, [setView]);

  if (loading) return <Spinner />;
  if (error) return <MessageBox message={error} />;

  /** --- Accordion reusable section --- */
  const AccordionSection = ({ title, isOpen, onToggle, children }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
      <div
        className="flex items-center justify-between p-4 bg-gray-100 cursor-pointer transition-colors duration-200 ease-in-out hover:bg-gray-200"
        onClick={onToggle}
      >
        <h2 className="text-lg font-medium text-gray-800">{title}</h2>
        <svg
          className={`w-5 h-5 text-gray-600 transform transition-transform duration-300 ease-in-out ${isOpen ? "rotate-180" : "rotate-0"
            }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isOpen ? "max-h-none opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="p-4 bg-white">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top title bar */}
      <div className="p-4 bg-white shadow">
        <h1 className="mb-4 text-xl font-bold text-center text-gray-900">
          {`Sales Order Details #${context?.id ?? ""} `}
        </h1>

      </div>

      <div className="flex justify-center mt-6">
        <div className="w-full max-w-5xl p-4 sm:p-6 bg-white shadow-lg rounded-xl">
          {/* Success message */}
          {success && <MessageBox message={success} type="success" onDismiss={() => setSuccess("")} />}

          {/* Accordion: Order Header */}
          {SalesOrderDetails && (
            <AccordionSection
              title="Sales Order Information"
              isOpen={isSalesOrderHeaderOpen}
              onToggle={() => setIsSalesOrderHeaderOpen(!isSalesOrderHeaderOpen)}
            >
              <SalesOrderHeader
                orderDetails={SalesOrderDetails}
                user={user}
                setSuccess={setSuccess}
                setError={setError}
                setOrderDetails={setSalesOrderDetails}
              />
            </AccordionSection>
          )}
          
          <AccordionSection
            title={`Sales Order Items (${salesOrderItems.length})`}
            isOpen={isSalesOrderItemsOpen}
            onToggle={() => setIsSalesOrderItemsOpen(!isSalesOrderItemsOpen)}
          >
            <SalesOrderItems
              orderId={context.id}
              customerId={context.customerId}
              orderDate={context.orderDate}
              items={salesOrderItems}
              user={user}
              setSuccess={setSuccess}
              setError={setError}
              refetchData={() => fetchOrderData(false)}
            />
          </AccordionSection>

          {/* Bottom Actions */}
          <div className="flex justify-between items-center pt-4 mt-6 border-t">
        <div className="flex gap-3">
            {/* ✅ New Quotation Button */}
    <Button type="button" onClick={handleQuotation} className="bg-green-500 hover:bg-green-600 px-3 py-1 text-sm"
>
  Quotation (F10) </Button>
      <Button onClick={triggerInvoiceGeneration}>
  {SalesOrderDetails?.InvNo && SalesOrderDetails.InvNo !== "InvNo :"
    ? SalesOrderDetails.InvNo
    : "Generate Invoice(F4)"}
</Button>
            </div>
            <Button
              type="button"
              onClick={() => setView("salesorders")}
              className="bg-pink-500 hover:bg-pink-600 px-4 py-2 text-white text-sm rounded"
            >
              Finish Sales Order
            </Button>
          </div>
        </div>
      </div>

      {/* PrintPreview Modal */}
      {showPrintPreview && printContent && (
        <PrintPreview
          htmlContent={printContent}
          onClose={() => {
            setShowPrintPreview(false);
            setPrintContent("");
            setCurrentOrder(null);
          }}
          order={currentOrder}
        />
      )}
    </div>
  );
};

export default SalesOrderDetailsPage;

// import React, { useState, useEffect, useCallback, useRef } from "react";
// import { useAuth } from "../../context/AuthContext";
// import api from "../../api/api";
// import html2canvas from "html2canvas";
// import Spinner from "../../components/ui/Spinner";
// import MessageBox from "../../components/ui/MessageBox";
// import Button from "../../components/ui/Button";
// import SalesOrderHeader from "./SalesOrderHeader";
// import SalesOrderItems from "./SalesOrderItems";
// import { XCircle, Printer, Send } from "lucide-react";
// import { jsPDF } from "jspdf";
// /** --- PrintPreview Component --- */
// const PrintPreview = ({ htmlContent, onClose, order }) => {
//   const hiddenRef = useRef(null);
//   const [waMenuOpen, setWaMenuOpen] = useState(false);
//   // Handle keyboard shortcuts
//   useEffect(() => {
//     const handleKeyDown = async (e) => {
//       if (e.key === "Escape") onClose();
//       else if (e.key === "F6") {
//         e.preventDefault();
//         handlePrint();
//       } else if (e.key === "F8") {
//         e.preventDefault();
//         await handleWhatsAppShare();
//       }
//     };
//     window.addEventListener("keydown", handleKeyDown);
//     return () => window.removeEventListener("keydown", handleKeyDown);
//   }, [order]);



//   const handlePrint = () => {
//     const iframe = document.getElementById("print-iframe");
//     if (iframe?.contentWindow) iframe.contentWindow.print();
//   };

//   // const handleWhatsAppShare = async () => {
//   //   if (!hiddenRef.current || !order?.MobileNo) return;
//   //   const mobileNumber = "91" + order.MobileNo.replace(/[^0-9]/g, "");
//   //   try {
//   //     const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true });
//   //     const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
//   //     if (blob && navigator.clipboard && window.ClipboardItem) {
//   //       await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
//   //     }
//   //     const message = encodeURIComponent("");
//   //     window.open(`https://wa.me/${mobileNumber}?text=${message}`, "_blank");
//   //   } catch (err) {
//   //     console.error("WhatsApp share failed:", err);
//   //   }
//   // };

  
//   const handleWhatsAppClick = () => {
//     setWaMenuOpen((prev) => !prev);
//   };

//   const shareAsImage = async () => {
//     setWaMenuOpen(false);
//     if (!hiddenRef.current) return alert("Content not ready.");

//     const countryCode = "91";
//     const mobileNumber = order?.MobileNo?.replace(/[^0-9]/g, "") || "";
//     if (!mobileNumber) return alert("Mobile number not available.");

//     const fullNumber = countryCode + mobileNumber;

//     try {
//       const canvas = await html2canvas(hiddenRef.current, {
//         scale: 2,
//         useCORS: true,
//         backgroundColor: null,
//       });
//       const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
//       if (blob) await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);

//       alert("Image copied! Paste in WhatsApp.");
//       window.open(`https://wa.me/${fullNumber}`, "_blank");
//     } catch (err) {
//       console.warn(err);
//     }
//   };

//   const shareAsPDF = async () => {
//     setWaMenuOpen(false);
//     if (!hiddenRef.current) return alert("Content not ready.");

//     try {
//       const canvas = await html2canvas(hiddenRef.current, { scale: 2, useCORS: true });
//       const doc = new jsPDF("p", "mm", "a4");
//       const imgData = canvas.toDataURL("image/png");

//       const pdfWidth = doc.internal.pageSize.getWidth();
//       const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
//       doc.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

//       const pdfBlob = doc.output("blob");
//       const file = new File([pdfBlob], "invoice.pdf", { type: "application/pdf" });

//       if (navigator.canShare && navigator.canShare({ files: [file] })) {
//         await navigator.share({
//           files: [file],
//           title: "Invoice PDF",
//           text: "Please find attached invoice PDF",
//         });
//       } else {
//         const blobUrl = URL.createObjectURL(pdfBlob);
//         alert("Your browser cannot share files directly. Please download the PDF and send via WhatsApp.");
//         window.open(blobUrl, "_blank");
//         setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
//       }
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   return (
//     <>
//       {/* Hidden copy for canvas */}
//       <div
//         ref={hiddenRef}
//         style={{ position: "fixed", top: "-9999px", left: "-9999px", width: "500px", background: "#fff" }}
//         dangerouslySetInnerHTML={{ __html: htmlContent }}
//       />
//       {/* Overlay */}
//       <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
//         <div className="relative w-full max-w-2xl p-4 bg-white rounded-lg shadow-2xl">
//           {/* Header */}
//           <h3 className="py-3 text-lg font-semibold text-center border-b">Invoice Preview</h3>
//           {/* Content */}
//           <div className="px-4 py-2 border-b">
//             <iframe
//               id="print-iframe"
//               srcDoc={htmlContent}
//               title="Print Preview"
//               style={{ width: "100%", height: "60vh", border: "none" }}
//             />
//           </div>
//           {/* Footer */}
//           <div className="flex justify-end gap-12 p-5">
//             <Button
//               onClick={onClose}
//               className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
//                 bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md 
//                 hover:from-red-600 hover:to-red-700 hover:shadow-lg 
//                 transition-all duration-300 ease-in-out transform hover:scale-105"
//             >
//               <XCircle size={16} /> Close
//             </Button>
//             <Button
//               onClick={handlePrint}
//               className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white
//                 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md
//                 hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg
//                 transition-all duration-300 ease-in-out transform hover:scale-105"
//             >
//               <Printer size={16} /> Print (F6)
//             </Button>
//             {/* <Button
//               onClick={handleWhatsAppShare}
//               className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white
//                 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-md
//                 hover:from-green-600 hover:to-emerald-700 hover:shadow-lg
//                 transition-all duration-300 ease-in-out transform hover:scale-105"
//             >
//               <Send size={16} /> WhatsApp (F8)
//             </Button> */}

//               {/* WhatsApp Button */}
//             <div className="relative">
//               <Button
//                 onClick={handleWhatsAppClick}
//                 className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white 
//       bg-green-500 rounded-lg shadow-md hover:bg-green-600 transition-all duration-300"
//   >
//                 <Send size={16} /> WhatsApp
//               </Button>

//               {waMenuOpen && (
//                 <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border rounded-lg shadow-lg w-44">
//                   <button
//                     onClick={shareAsImage}
//                     className="flex items-center w-full gap-2 px-3 py-2 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600"
//                   >
//                     <img src="/icons/image-icon.svg" alt="image" className="w-4 h-4" /> Share as Image
//                   </button>

//                   <button
//                     onClick={shareAsPDF}
//                     className="flex items-center w-full gap-2 px-3 py-2 mt-1 text-sm font-medium text-white transition-all duration-200 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600"
//                   >
//                     <img src="/icons/pdf-icon.svg" alt="pdf" className="w-4 h-4" /> Share as PDF
//                   </button>
//                 </div>
//               )}
//             </div>

//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// /** --- Main Component --- */
// const SalesOrderDetailsPage = ({ setView, context }) => {
//   const { user } = useAuth();
//   const [SalesOrderDetails, setSalesOrderDetails] = useState(null);
//   const [salesOrderItems, setSalesOrderItems] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [success, setSuccess] = useState("");

//   // Accordion states
//   const [isSalesOrderHeaderOpen, setIsSalesOrderHeaderOpen] = useState(false);
//   const [isSalesOrderItemsOpen, setIsSalesOrderItemsOpen] = useState(true);

//   // Print preview state
//   const [showPrintPreview, setShowPrintPreview] = useState(false);
//   const [printContent, setPrintContent] = useState("");
//   const [currentOrder, setCurrentOrder] = useState(null);

//   /** --- Inside fetchOrderData --- */
// const fetchOrderData = useCallback(async (showSpinner = true) => {
//   if (showSpinner) setLoading(true);
//   try {
//     const orderData = await api.getSalesOrderForEdit(context.id);

//     // Depending on API, sometimes data is under EditPoOrderTaxjs.Table
//     const table = orderData?.EditPoOrderTaxjs?.Table || orderData?.Table || [];
//     const table1 = orderData?.EditPoOrderTaxjs?.Table1 || orderData?.Table1 || [];
//     const table2 = orderData?.EditPoOrderTaxjs?.Table2 || orderData?.Table2 || [];
//     const table3 = orderData?.EditPoOrderTaxjs?.Table3 || orderData?.Table3 || [];

//     if (table.length > 0) {
//       const header = table[0];

//       // ✅ Convert Podate and DeliveryDate to YYYY-MM-DD
//       const formatDate = (dateString) => {
//         if (!dateString) return "";
//         const d = new Date(dateString);
//         const month = (d.getMonth() + 1).toString().padStart(2, "0");
//         const day = d.getDate().toString().padStart(2, "0");
//         const year = d.getFullYear();
//         return `${year}-${month}-${day}`;
//       };

//       const fixedHeader = {
//         ...header,
//         Podate: formatDate(header.Podate),
//         DeliveryDate: formatDate(header.DeliveryDate),
//         SubTypeCode: header.SubTypeCode,
//         SubTypeName: header.SubTypeName,
//       };

//       setSalesOrderDetails({
//         ...fixedHeader,
//         Table1: table1,
//         Table2: table2,
//         Table3: table3,
//       });
//       setSalesOrderItems(table1);
//     }
//   } catch (err) {
//     console.error(err);
//     setError("Failed to load order data.");
//   } finally {
//     if (showSpinner) setLoading(false);
//   }
// }, [context.id]);


//   useEffect(() => {
//     fetchOrderData();
//   }, [fetchOrderData]);

//   /** --- Generate Invoice & Open Preview --- */
//   const handlePrintInvoice = async () => {
//   try {
//     const resp = await api.MobGetfinalizeOrder(context.id);
//     const content =
//       resp?.Table?.[0]?.PrintContent ??
//       resp?.Table3?.[0]?.PrintContent ??
//       resp?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ??
//       resp?.data?.MobGetfinalizeOrderjs?.Table?.[0]?.PrintContent ??
//       null;

//     if (!content) return alert("Print content not found.");

//     setPrintContent(content);

//     // Update current order state
//     const newOrder = SalesOrderDetails
//       ? { ...SalesOrderDetails, Poid: context.id }
//       : { Poid: context.id };
//     setCurrentOrder(newOrder);

//     // Extract Bill No (Invoice number)
//     const match = content.match(/Bill No\s*-?\s*([^*<\r\n]+)/i);
//     if (match) {
//       const invNo = match[1].trim();

//       // ✅ Update SalesOrderDetails with the new invoice info
//       setSalesOrderDetails((prev) =>
//         prev
//           ? { ...prev, InvNo: invNo, Cpid: context.id } // Make sure Cpid is set
//           : { Poid: context.id, InvNo: invNo, Cpid: context.id }
//       );
//     }

//     setShowPrintPreview(true);
//   } catch (err) {
//     console.error(err);
//     alert("Error fetching print content.");
//   }
// };
// const handleQuotation = async () => {
//   try {
//     const resp = await api.get(`GetHtmlPoQuotationAPI?PoId=${context.id}`);
//     console.log("Quotation API Response:", resp);

//     const content = resp?.Table?.[0]?.PoQuotationHtml ?? null;

//     if (content) {
//       setPrintContent(content);

//       // Use the correct state variable
//       const curOrder = SalesOrderDetails
//         ? { ...SalesOrderDetails, Poid: context.id }
//         : { Poid: context.id };

//       setCurrentOrder(curOrder);
//       setShowPrintPreview(true);
//     } else {
//       alert("Quotation content not found.");
//     }
//   } catch (err) {
//     console.error("Error fetching quotation:", err);
//     alert("Failed to fetch quotation.");
//   }
// };
 
// const triggerInvoiceGeneration = async () => {
//   const curOrder = SalesOrderDetails || {};
//   const invoiceExists = curOrder.Cpid && Number(curOrder.Cpid) > 0;

//   if (!invoiceExists) {
//     if (window.confirm("Invoice not generated yet. Do you want to generate?")) {
//       await handlePrintInvoice();
//     }
//   } else {
//     await handlePrintInvoice();
//   }
// };

// // --- F10 Shortcut for Quotation ---
// useEffect(() => {
//   const handleF10 = (e) => {
//     if (e.key === "F10") {
//       e.preventDefault();
//       handleQuotation();
//     }
//   };

//   window.addEventListener("keydown", handleF10);
//   return () => window.removeEventListener("keydown", handleF10);
// }, [SalesOrderDetails]); // depend on current order if needed

//   useEffect(() => {
//   const handleF4 = (e) => {
//     if (e.key === "F4") {
//       e.preventDefault();
//       triggerInvoiceGeneration(); // ✅ now shows confirm alert
//     }
//   };

//   window.addEventListener("keydown", handleF4);
//   return () => window.removeEventListener("keydown", handleF4);
// }, [SalesOrderDetails]);


//   useEffect(() => {
//     const handleF7 = (e) => {
//       if (e.key === "F7") {
//         e.preventDefault();
//         setView("salesorders");
//       }
//     };
//     window.addEventListener("keydown", handleF7);
//     return () => window.removeEventListener("keydown", handleF7);
//   }, [setView]);

//   if (loading) return <Spinner />;
//   if (error) return <MessageBox message={error} />;

//   /** --- Accordion reusable section --- */
//   const AccordionSection = ({ title, isOpen, onToggle, children }) => (
//     <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
//       <div
//         className="flex items-center justify-between p-4 bg-gray-100 cursor-pointer transition-colors duration-200 ease-in-out hover:bg-gray-200"
//         onClick={onToggle}
//       >
//         <h2 className="text-lg font-medium text-gray-800">{title}</h2>
//         <svg
//           className={`w-5 h-5 text-gray-600 transform transition-transform duration-300 ease-in-out ${isOpen ? "rotate-180" : "rotate-0"
//             }`}
//           xmlns="http://www.w3.org/2000/svg"
//           fill="none"
//           viewBox="0 0 24 24"
//           stroke="currentColor"
//         >
//           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
//         </svg>
//       </div>
//       <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isOpen ? "max-h-none opacity-100" : "max-h-0 opacity-0"}`}>
//         <div className="p-4 bg-white">{children}</div>
//       </div>
//     </div>
//   );

//   return (
//     <div className="min-h-screen bg-gray-50">
//       {/* Top title bar */}
//       <div className="p-4 bg-white shadow">
//         <h1 className="mb-4 text-xl font-bold text-center text-gray-900">
//           {`Sales Order Details #${context?.id ?? ""} `}
//         </h1>

//       </div>

//       <div className="flex justify-center mt-6">
//         <div className="w-full max-w-5xl p-4 sm:p-6 bg-white shadow-lg rounded-xl">
//           {/* Success message */}
//           {success && <MessageBox message={success} type="success" onDismiss={() => setSuccess("")} />}

//           {/* Accordion: Order Header */}
//           {SalesOrderDetails && (
//             <AccordionSection
//               title="Sales Order Information"
//               isOpen={isSalesOrderHeaderOpen}
//               onToggle={() => setIsSalesOrderHeaderOpen(!isSalesOrderHeaderOpen)}
//             >
//               <SalesOrderHeader
//                 orderDetails={SalesOrderDetails}
//                 user={user}
//                 setSuccess={setSuccess}
//                 setError={setError}
//                 setOrderDetails={setSalesOrderDetails}
//               />
//             </AccordionSection>
//           )}

//           {/* Accordion: Order Items */}
//           <AccordionSection
//             title={`Sales Order Items (${salesOrderItems.length})`}
//             isOpen={isSalesOrderItemsOpen}
//             onToggle={() => setIsSalesOrderItemsOpen(!isSalesOrderItemsOpen)}
//           >
//             <SalesOrderItems
//               orderId={context.id}
//               customerId={context.customerId}
//               orderDate={context.orderDate}
//               items={salesOrderItems}
//               user={user}
//               setSuccess={setSuccess}
//               setError={setError}
//               refetchData={() => fetchOrderData(false)}
//             />
//           </AccordionSection>

//           {/* Bottom Actions */}
//           <div className="flex justify-between items-center pt-4 mt-6 border-t">
//         <div className="flex gap-3">
//             {/* ✅ New Quotation Button */}
//     <Button
//   type="button"
//   onClick={handleQuotation}
//   className="bg-green-500 hover:bg-green-600 px-3 py-1 text-sm"
// >
//   Quotation (F10)
// </Button>
//       <Button onClick={triggerInvoiceGeneration}>
//   {SalesOrderDetails?.InvNo && SalesOrderDetails.InvNo !== "InvNo :"
//     ? SalesOrderDetails.InvNo
//     : "Generate Invoice(F4)"}
// </Button>
//             </div>
//             <Button
//               type="button"
//               onClick={() => setView("salesorders")}
//               className="bg-pink-500 hover:bg-pink-600 px-4 py-2 text-white text-sm rounded"
//             >
//               Finish Sales Order
//             </Button>
//           </div>
//         </div>
//       </div>

//       {/* PrintPreview Modal */}
//       {showPrintPreview && printContent && (
//         <PrintPreview
//           htmlContent={printContent}
//           onClose={() => {
//             setShowPrintPreview(false);
//             setPrintContent("");
//             setCurrentOrder(null);
//           }}
//           order={currentOrder}
//         />
//       )}
//     </div>
//   );
// };

// export default SalesOrderDetailsPage;
